import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { Icon } from '@sb/design-system';

import type { ServerToolItem } from './types';

interface ServerToolGroupProps {
  /** Initial tool sequence — usually all `'pending'`. */
  tools: ServerToolItem[];
  /**
   * Per-step delay in ms between status transitions. Item N becomes
   * running, then `delays[N]` ms later becomes complete. Defaults to
   * 300ms per step when not provided.
   */
  delays?: number[];
  /**
   * When true, the group is fully driven by the `tools` prop — no
   * internal animation timers and no auto-collapse. State syncs to
   * the latest `tools` array on every prop change so live tool_use /
   * tool_result events from the orchestrator can flip rows from
   * 'running' to 'complete' / 'error' in place. Default false
   * preserves the legacy uncontrolled / animated behavior used by
   * mock fixtures.
   */
  controlled?: boolean;
}

const DEFAULT_STEP_DELAY_MS = 300;

const ICON_COLOR = 'var(--label-tertiary)';

function StatusIcon({ status }: { status: ServerToolItem['status'] }) {
  switch (status) {
    case 'complete':
      return (
        <Icon
          kind="checkmark"
          className="tw-shrink-0"
          // 14×14 isn't in the icon-size scale; muted icon color stays inline.
          style={{ width: 14, height: 14, color: ICON_COLOR }}
        />
      );
    case 'running':
      // Hand-rolled spinner so the stroke / sizing stays exact across
      // status transitions. The design-system Icon component doesn't
      // ship a spinner glyph. `tw-animate-spin` re-uses Tailwind's
      // built-in `@keyframes spin` definition.
      return (
        <div
          className="tw-animate-spin tw-shrink-0"
          style={{
            width: 14,
            height: 14,
            border: `2px solid ${ICON_COLOR}`,
            borderTopColor: 'transparent',
            borderRadius: '50%',
          }}
        />
      );
    case 'error':
      // Reuse the design-system X to stay neutral — error status keeps
      // the same muted color, the glyph alone signals "didn't finish".
      return (
        <Icon
          kind="xmark"
          className="tw-shrink-0"
          style={{ width: 14, height: 14, color: ICON_COLOR }}
        />
      );
    case 'pending':
    default:
      return (
        <div
          className="tw-shrink-0"
          style={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            border: `1.5px solid ${ICON_COLOR}`,
            opacity: 0.5,
          }}
        />
      );
  }
}

/**
 * "Showing my work" tool-run summary, Claude Code style. Each tool
 * row is a muted icon + a short text label describing the action
 * the agent took. All icons render in the same neutral color (no
 * green / red status coding); the icon SHAPE conveys status.
 *
 * The card auto-animates from pending → running → complete in
 * sequence, then collapses to a single summary line ("Built N
 * steps"). The user can expand it again to see the per-step list.
 *
 * All icons render in the same muted color so the row stays
 * visually quiet against the chat background; status is conveyed
 * by the icon SHAPE (spinner / checkmark / x), not by color.
 */
export function ServerToolGroup({
  tools: initialTools,
  delays = [],
  controlled = false,
}: ServerToolGroupProps) {
  const [tools, setTools] = useState<ServerToolItem[]>(initialTools);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // In controlled mode the parent owns the status — re-sync local
  // state whenever the prop changes so live tool_result events flip
  // the row in place. Skipped in uncontrolled mode so the existing
  // mock-driven animation isn't clobbered.
  useEffect(() => {
    if (!controlled) return;
    setTools(initialTools);
  }, [controlled, initialTools]);

  // Controlled-mode auto-collapse: once every row reaches `complete`,
  // collapse to a single summary line — matches the uncontrolled /
  // mock animation's end-of-sequence behavior. If any row errored,
  // keep the group expanded so the failure stays visible.
  useEffect(() => {
    if (!controlled) return undefined;
    if (tools.length === 0) return undefined;

    const allDone = tools.every((t) => t.status === 'complete');

    if (!allDone) return undefined;

    const id = setTimeout(() => setIsCollapsed(true), 300);

    return () => clearTimeout(id);
  }, [controlled, tools]);

  // Every timeout we schedule lands here so the cleanup effect can
  // clear them on unmount. Without this, a card that scrolls out of
  // the panel mid-animation will keep firing setState on a dead
  // component.
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Schedule the running → complete transitions once on mount.
  // `initialTools` and `delays` are snapshot-at-mount by design — the
  // component does not respond to prop changes after the first render.
  // The empty dep array is intentional: a parent re-render that passes
  // new array literals must NOT tear down the in-flight timeouts
  // (which `[initialTools, delays]` deps did, freezing the animation
  // mid-sequence).
  //
  // Cleanup clears the scheduled timeouts AND resets the ref so React
  // 18 Strict Mode's dev-only unmount-then-remount cycle replays the
  // animation cleanly — without the reset, the second mount would
  // re-use the cleared timeout array and the animation would stall
  // with every row stuck in `pending`.
  useEffect(() => {
    if (controlled) return undefined;

    let cumulativeDelay = 0;

    initialTools.forEach((_tool, index) => {
      const stepDelay = delays[index] ?? DEFAULT_STEP_DELAY_MS;

      timeoutsRef.current.push(
        setTimeout(() => {
          setTools((prev) =>
            prev.map((t, i) => (i === index ? { ...t, status: 'running' } : t)),
          );
        }, cumulativeDelay),
      );

      cumulativeDelay += stepDelay;

      timeoutsRef.current.push(
        setTimeout(() => {
          setTools((prev) =>
            prev.map((t, i) =>
              i === index ? { ...t, status: 'complete' } : t,
            ),
          );

          // Auto-collapse 300ms after the last item finishes so the
          // user sees the final state before it folds.
          if (index === initialTools.length - 1) {
            timeoutsRef.current.push(
              setTimeout(() => setIsCollapsed(true), 300),
            );
          }
        }, cumulativeDelay),
      );
    });

    return () => {
      timeoutsRef.current.forEach((id) => clearTimeout(id));
      timeoutsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally mount-only; see comment above
  }, []);

  const completedCount = tools.filter((t) => t.status === 'complete').length;
  const allComplete = completedCount === tools.length;

  if (isCollapsed && allComplete) {
    return (
      <button
        type="button"
        onClick={() => setIsCollapsed(false)}
        className={cx(
          'tw-flex',
          'tw-items-center',
          'tw-gap-8',
          'tw-bg-transparent',
          'tw-border-none',
          'tw-p-0',
          'tw-m-0',
          'tw-text-13',
          'tw-cursor-pointer',
          'tw-text-left',
        )}
        style={{
          // 55%-black collapsed-summary color doesn't have a token at
          // this size; inherited font family stays inline.
          fontFamily: 'inherit',
          color: 'var(--label-secondary)',
        }}
      >
        <Icon
          kind="checkmark"
          className="tw-shrink-0"
          style={{
            width: 14,
            height: 14,
            color: 'var(--label-tertiary)',
          }}
        />
        <span>
          {tools.length === 1 ? '1 tool ran' : `${tools.length} tools ran`}
        </span>
        <span className="tw-ml-2" style={{ color: 'var(--label-quaternary)' }}>
          ▸
        </span>
      </button>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {allComplete && (
        <button
          type="button"
          onClick={() => setIsCollapsed(true)}
          className={cx(
            'tw-inline-flex',
            'tw-items-center',
            'tw-bg-transparent',
            'tw-border-none',
            'tw-p-0',
            'tw-cursor-pointer',
            'tw-text-left',
          )}
          style={{
            // 12px font + 30%-black collapse-link color + inherited
            // font family don't have tokens; bottom margin 2px stays
            // inline (the spacing scale starts at 2 but `tw-mb-2`
            // refers to 2px — leave inline for the asymmetric `0 0 2 0`).
            margin: '0 0 2px 0',
            fontFamily: 'inherit',
            fontSize: 12,
            color: 'var(--label-quaternary)',
          }}
        >
          ▾ Collapse
        </button>
      )}
      {tools.map((tool) => (
        <div
          key={tool.id}
          className="tw-flex tw-items-center tw-gap-8 tw-text-13"
          style={{
            color: 'var(--label-secondary)',
          }}
        >
          <StatusIcon status={tool.status} />
          <span>{tool.label}</span>
        </div>
      ))}
    </div>
  );
}
